import { KeyRound, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type ManagedKey,
  type ManagedKeyProvider,
  type SetManagedKeyInput,
  useDeleteManagedKey,
  useManagedKeys,
  useSetManagedKey,
} from '@/shared/api/admin-managed-keys';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

const PROVIDERS: { value: ManagedKeyProvider; label: string }[] = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
];

const PROVIDER_LABEL: Record<ManagedKeyProvider, string> = {
  openrouter: 'OpenRouter',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
};

export function ManagedKeysPage() {
  const { data, isLoading } = useManagedKeys();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ManagedKey | null>(null);
  const deleteMut = useDeleteManagedKey();

  return (
    <AdminLayout>
      <header className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Managed AI keys</h1>
          <p className="text-sm text-neutral-400">
            Provider keys WE pay for (&ldquo;managed&rdquo; mode). Used when a team is on managed
            AI — folded into the signed call to the AI core, so a swap takes effect on the next
            request without a redeploy.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
        >
          <Plus size={14} /> Add / rotate key
        </button>
      </header>

      <div className="mb-5 flex items-start gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 p-3 text-xs text-neutral-400">
        <ShieldCheck size={15} className="mt-0.5 shrink-0 text-emerald-400" />
        <p>
          Keys are encrypted at rest (AES-256-GCM) and <strong className="text-neutral-200">write-only</strong> —
          once saved, the value can never be viewed again, only the last 4 characters. Adding a key for a
          provider replaces its current active key (the old one is kept inactive for the audit trail).
        </p>
      </div>

      {isLoading && !data ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : (
        <div className="space-y-3">
          {(data ?? []).map((k) => (
            <KeyCard key={k.id} k={k} onDelete={() => setDeleteTarget(k)} />
          ))}
          {data && data.length === 0 ? (
            <p className="text-sm text-neutral-500">
              No managed keys yet. Add an OpenRouter key to power managed AI.
            </p>
          ) : null}
        </div>
      )}

      <AddKeyDialog open={addOpen} onOpenChange={setAddOpen} />

      {deleteTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title={`Delete this ${PROVIDER_LABEL[deleteTarget.provider]} key?`}
          description="Removes the key permanently. If it was the active key, managed AI for that provider stops working until you add another. Audit-logged."
          confirmLabel="Delete"
          tone="danger"
          pending={deleteMut.isPending}
          onConfirm={async (reason) => {
            await deleteMut.mutateAsync({ id: deleteTarget.id, reason });
            toast.success('Managed key deleted');
            setDeleteTarget(null);
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

function KeyCard(props: { k: ManagedKey; onDelete: () => void }) {
  const k = props.k;
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <KeyRound size={15} className="text-orange-400" />
            <span className="text-sm font-medium text-neutral-100">
              {PROVIDER_LABEL[k.provider]}
            </span>
            <code className="rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300">
              {k.keyHint ? `••••${k.keyHint}` : '••••'}
            </code>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
                k.isActive
                  ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30'
                  : 'bg-neutral-700/30 text-neutral-500 ring-neutral-700',
              )}
            >
              {k.isActive ? 'active' : 'inactive'}
            </span>
          </div>
          {k.label ? <p className="mt-1 text-sm text-neutral-400">{k.label}</p> : null}
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-500">
            <span>Added {new Date(k.createdAt).toLocaleDateString()}</span>
            <span>
              Last used {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'never'}
            </span>
          </div>
        </div>
        <button
          onClick={props.onDelete}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-red-400 hover:bg-red-950 hover:text-red-300"
        >
          <Trash2 size={11} /> Delete
        </button>
      </div>
    </div>
  );
}

function AddKeyDialog(props: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const setMut = useSetManagedKey();
  const [provider, setProvider] = useState<ManagedKeyProvider>('openrouter');
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('');

  if (!props.open) return null;

  const keyOk = key.trim().length >= 8;
  const reasonOk = reason.trim().length >= 10;
  const canSubmit = keyOk && reasonOk && !setMut.isPending;

  function reset() {
    setProvider('openrouter');
    setKey('');
    setLabel('');
    setReason('');
  }

  async function submit() {
    if (!canSubmit) return;
    const input: SetManagedKeyInput = { provider, key: key.trim(), reason: reason.trim() };
    if (label.trim()) input.label = label.trim();
    try {
      await setMut.mutateAsync(input);
      toast.success(`${PROVIDER_LABEL[provider]} key saved`);
      props.onOpenChange(false);
      reset();
    } catch {
      /* toast'ed by the mutation cache */
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl bg-neutral-900 p-6 shadow-2xl ring-1 ring-neutral-800">
        <h2 className="mb-1 text-base font-semibold text-neutral-100">Add / rotate managed key</h2>
        <p className="mb-4 text-sm text-neutral-400">
          Pasting a key for a provider replaces its current active key. The value is encrypted and
          never shown again — keep a copy somewhere safe.
        </p>

        <div className="mb-3">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            Provider
          </label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as ManagedKeyProvider)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          >
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            API key
          </label>
          <input
            type="password"
            autoComplete="off"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="sk-or-v1-…"
            className="w-full rounded-md bg-neutral-800 px-3 py-2 font-mono text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
          {key && !keyOk ? (
            <div className="mt-1 text-xs text-red-400">Key looks too short.</div>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            Label (optional)
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. OpenRouter prod"
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="mb-4">
          <label className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">
            Reason (≥10 chars; recorded in audit log)
          </label>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Briefly explain why."
            className="w-full resize-none rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-800 pt-4">
          <button
            onClick={() => props.onOpenChange(false)}
            className="rounded-md px-3 py-1.5 text-sm text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="rounded-md bg-orange-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
          >
            {setMut.isPending ? 'Saving…' : 'Save key'}
          </button>
        </div>
      </div>
    </div>
  );
}
